/**
 * Shows notification params
 * @constructor
 * @param {string} type - Type should be one of this "success, waring, error"
 * @param {string} title - title of notification
 * @param {boolean} autoClose - If set to false, the notification needs to be closed manually
 * @param {number} delay - removed after some milliseconds
 * @param {string} position - Position should be one of this 'top-right, top-left, top-center, bottom-left, bottom-center, bottom-right'
 */

import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";

const mountToastifyRoot = () => {
  if (document.querySelector(".Toastify")) return;
  const host = document.body || document.documentElement;
  if (!host) return;
  host.prepend(_$("<div>").addClass("Toastify").el());
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", mountToastifyRoot, {
    once: true,
  });
} else {
  mountToastifyRoot();
}

const renderIcon = {
  success: '<i class="icon-ok-circle"></i>',
  error: '<i class="icon-exclamation-sign"></i>',
  warning: '<i class="icon-info-sign"></i>',
};

let currentNotifications = {};

const sanitizeToastText = (value) => {
  let safe = value;
  if (typeof utils.sanitizeWithKeepingOnlyFormatting === "function") {
    safe = utils.sanitizeWithKeepingOnlyFormatting(value);
  } else if (typeof utils.sanitizePure === "function") {
    safe = utils.sanitizePure(value);
  } else if (typeof utils.sanitize === "function") {
    safe = utils.sanitize(value);
  } else {
    safe = value ? String(value).replace(/<[^>]*>?/gm, "") : "";
  }
  return typeof safe === "string" ? safe.trim() : "";
};

export const toast = ({
  type = "success",
  position = "top-right",
  title,
  message,
  autoClose = false,
  delay = 3000,
}) => {
  const safeTitle = sanitizeToastText(title);
  const safeMessage = sanitizeToastText(message);

  if (!safeTitle && !safeMessage) {
    return;
  }

  const toastType = renderIcon[type] ? type : "success";
  const currentNotificationKey = `${safeTitle}_${safeMessage}_${toastType}_${position}`;

  if (currentNotifications[currentNotificationKey]) {
    return;
  }

  const uniqueId = utils.getUId();

  let toastifyContainer = _$(
    `.Toastify__toast-container.Toastify__toast-container--${position}`
  ).el();

  if (!toastifyContainer) {
    mountToastifyRoot();
    const toastifyRoot = document.querySelector(".Toastify");
    if (!toastifyRoot) {
      return;
    }
    toastifyContainer = document.createElement("div");
    toastifyContainer.classList.add(
      "Toastify__toast-container",
      `Toastify__toast-container--${position}`
    );
    toastifyRoot.insertAdjacentElement("afterbegin", toastifyContainer);
  }

  const closeNotification = (id) => {
    if (id) {
      const toastElement = document.getElementById(id);
      if (toastElement) {
        const instantDismiss =
          document.body?.getAttribute("data-electron-upload-host") === "true";
        if (instantDismiss) {
          toastElement.remove();
          delete currentNotifications[currentNotificationKey];
          return;
        }
        toastElement.classList.add("remove-animation");
        toastElement.addEventListener("animationend", () => {
          toastElement.remove();
          delete currentNotifications[currentNotificationKey];
        });
      }
    }
  };

  const toastDiv = document.createElement("div");
  toastDiv.classList.add("toast", `toast-${toastType}`);
  toastDiv.setAttribute("id", uniqueId);

  const iconDiv = document.createElement("div");
  iconDiv.classList.add("toast-icon");
  iconDiv.innerHTML = renderIcon[toastType];

  const bodyDiv = document.createElement("div");
  bodyDiv.classList.add(
    "toast-body",
    "text-sm",
    "is-flex-grow-1",
    "text-primaryMain"
  );
  if (safeMessage) {
    const messageEl = document.createElement("p");
    messageEl.classList.add("text-sm", "has-text-weight-semibold");
    messageEl.innerHTML = safeMessage;
    bodyDiv.appendChild(messageEl);
  }
  if (safeTitle) {
    const titleEl = document.createElement("p");
    titleEl.classList.add("text-sm", "word-break-all");
    titleEl.innerHTML = safeTitle;
    bodyDiv.appendChild(titleEl);
  }

  const closeButton = document.createElement("button");
  closeButton.classList.add("toast-close-btn");
  closeButton.type = "button";
  closeButton.setAttribute("aria-label", "Close");
  closeButton.innerHTML = '<i class="icon-remove"></i>';
  closeButton.onclick = () => {
    closeNotification(uniqueId);
  };

  toastDiv.appendChild(iconDiv);
  toastDiv.appendChild(bodyDiv);
  toastDiv.appendChild(closeButton);

  toastifyContainer.insertAdjacentElement("beforeend", toastDiv);

  currentNotifications[currentNotificationKey] = uniqueId;

  if (autoClose) {
    window.setTimeout(closeNotification.bind(null, uniqueId), delay);
  }
};

export const growl = (title, message, timeout, error) => {
  const electronCompact =
    document.body?.getAttribute("data-electron-upload-host") === "true";

  toast({
    title,
    message,
    delay: timeout ? timeout : undefined,
    autoClose: timeout ? true : false,
    type: error ? "error" : "success",
    position: electronCompact ? "bottom-center" : "top-right",
  });
};
